import React from 'react';

/**
 * SpeedoGauge Component
 * A semi-circular speedometer-style gauge for visualizing readiness index
 * 
 * @param {number} value - Current value (0.00 to 1.00)
 * @param {string} status - Status text (e.g., "GREEN", "AMBER", "RED")
 * @param {Object} thresholds - Threshold boundaries { green: 0.33, amber: 0.66 }
 */
const SpeedoGauge = ({ value = 0, status = '', thresholds = { green: 0.33, amber: 0.66 } }) => {
  // Ensure value is within bounds
  const clampedValue = Math.max(0, Math.min(1, value));
  
  // SVG dimensions
  const width = 280;
  const height = 160;
  const centerX = width / 2;
  const centerY = height - 20;
  const radius = 100;
  const strokeWidth = 24;
  
  // Arc radii for marker positioning
  const arcOuterRadius = radius + strokeWidth / 2;
  const arcInnerRadius = radius - strokeWidth / 2;
  
  // Calculate arc paths for each zone
  const startAngle = 180; // Start at left (180 degrees)
  const endAngle = 0; // End at right (0 degrees)
  const totalAngle = 180; // Semi-circle
  
  // Convert thresholds to angles
  const greenEndAngle = startAngle - (thresholds?.green * totalAngle);
  const amberEndAngle = startAngle - (thresholds?.amber * totalAngle);
  
  // Helper function to convert angle to coordinates
  const polarToCartesian = (angle) => {
    const radians = (angle * Math.PI) / 180;
    return {
      x: centerX + radius * Math.cos(radians),
      y: centerY - radius * Math.sin(radians)
    };
  };
  
  // Helper function to create arc path
  const createArc = (startAngle, endAngle) => {
    const start = polarToCartesian(startAngle);
    const end = polarToCartesian(endAngle);
    const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
    
    return [
      `M ${start?.x} ${start?.y}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end?.x} ${end?.y}`
    ]?.join(' ');
  };
  
  // Create arc paths for each zone
  const greenArc = createArc(startAngle, greenEndAngle);
  const amberArc = createArc(greenEndAngle, amberEndAngle);
  const redArc = createArc(amberEndAngle, endAngle);
  
  // Calculate marker position using correct angle mapping
  // startAngle = -180 degrees (left), endAngle = 0 degrees (right)
  const markerAngleDeg = -180 + clampedValue * 180;
  const markerAngleRad = (Math.PI / 180) * markerAngleDeg;
  
  // Calculate marker tick line endpoints (crosses arc only)
  const rOuter = arcOuterRadius + 2;
  const rInner = arcInnerRadius - 2;
  
  const markerX1 = centerX + rInner * Math.cos(markerAngleRad);
  const markerY1 = centerY + rInner * Math.sin(markerAngleRad);
  const markerX2 = centerX + rOuter * Math.cos(markerAngleRad);
  const markerY2 = centerY + rOuter * Math.sin(markerAngleRad);
  
  // Get status color
  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase();
    if (statusUpper === 'GREEN') return '#22C55E';
    if (statusUpper === 'AMBER') return '#F59E0B';
    if (statusUpper === 'RED') return '#EF4444';
    return '#6B7280';
  };
  
  const statusColor = getStatusColor(status);
  
  // Label positions
  const minLabelPos = polarToCartesian(startAngle);
  const maxLabelPos = polarToCartesian(endAngle);
  
  return (
    <div 
      className="flex flex-col items-center justify-center w-full"
      role="img"
      aria-label={`Readiness Index gauge, value ${value?.toFixed(2)}, status ${status}`}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${width} ${height}`}
        className="max-w-full"
        style={{ maxWidth: '280px' }}
      >
        {/* Green zone */}
        <path
          d={greenArc}
          fill="none"
          stroke="#22C55E"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Amber zone */}
        <path
          d={amberArc}
          fill="none"
          stroke="#F59E0B"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Red zone */}
        <path
          d={redArc}
          fill="none"
          stroke="#EF4444"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Min label (0) */}
        <text
          x={minLabelPos?.x}
          y={minLabelPos?.y + 20}
          textAnchor="start"
          className="text-xs font-semibold fill-gray-600"
        >
          0
        </text>
        
        {/* Max label (1) */}
        <text
          x={maxLabelPos?.x}
          y={maxLabelPos?.y + 20}
          textAnchor="end"
          className="text-xs font-semibold fill-gray-600"
        >
          1
        </text>
        
        {/* Arc marker - radial tick line that crosses arc only */}
        <line
          x1={markerX1}
          y1={markerY1}
          x2={markerX2}
          y2={markerY2}
          stroke="#1F2937"
          strokeWidth="4"
          strokeLinecap="round"
          style={{
            transition: 'x1 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), y1 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), x2 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), y2 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        />
        
        {/* White outline for better visibility on all segments */}
        <line
          x1={markerX1}
          y1={markerY1}
          x2={markerX2}
          y2={markerY2}
          stroke="white"
          strokeWidth="6"
          strokeLinecap="round"
          opacity="0.8"
          style={{
            transition: 'x1 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), y1 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), x2 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), y2 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        />
        
        {/* Dark marker on top */}
        <line
          x1={markerX1}
          y1={markerY1}
          x2={markerX2}
          y2={markerY2}
          stroke="#1F2937"
          strokeWidth="3"
          strokeLinecap="round"
          style={{
            transition: 'x1 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), y1 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), x2 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), y2 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        />
        
        {/* Center value and status label - unobstructed by marker */}
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          className="text-2xl font-bold"
          fill={statusColor}
        >
          {value?.toFixed(2)}
        </text>
        
        <text
          x={centerX}
          y={centerY + 10}
          textAnchor="middle"
          className="text-sm font-semibold"
          fill={statusColor}
        >
          {status}
        </text>
      </svg>
    </div>
  );
};

export default SpeedoGauge;